import { ArrowRight } from 'react-feather';

import { cn } from '~lib/utils/style';

import type { DatasetSummary } from '~modules/search/types';

import { Card } from '~ui/base/card';
import { Typography } from '~ui/base/typography';

interface SearchResultCardProps {
  dataset: DatasetSummary;
}

const KEYWORD_LIMIT = 5;
const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatDate(iso: string): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return DATE_FORMATTER.format(parsed);
}

function SearchResultCard({ dataset }: SearchResultCardProps) {
  const visibleKeywords = dataset.keywords.slice(0, KEYWORD_LIMIT);
  const remainingKeywords = dataset.keywords.length - visibleKeywords.length;

  return (
    <Card
      as="a"
      ornament="outline"
      interactive
      // Use the doi.org URL from the response — survives platform-domain
      // changes and matches the citable URL readers will recognise.
      {...{ href: dataset.doi_url, target: '_blank', rel: 'noopener noreferrer' }}
      className={cn('group flex flex-col gap-y-4 no-underline')}
    >
      <header className="flex items-start justify-between gap-x-6">
        <div className="flex flex-col gap-y-1">
          <Typography variant="caption-md" as="span" className="text-muted-foreground">
            <span className="font-mono">{dataset.doi}</span>
            <span aria-hidden className="px-1.5">·</span>
            <span>{dataset.corresponding_author}</span>
            <span aria-hidden className="px-1.5">·</span>
            <span>{formatDate(dataset.published_on)}</span>
          </Typography>
          <Typography variant="headline-sm" as="h2" className="text-foreground">
            {dataset.title}
          </Typography>
        </div>
        <ArrowRight
          aria-hidden
          className="mt-1 size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary"
        />
      </header>

      {dataset.description && (
        <Typography
          variant="body-md"
          as="p"
          className="clamp-3 text-muted-foreground-dark"
        >
          {dataset.description}
        </Typography>
      )}

      <footer className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {visibleKeywords.length > 0 && (
          <ul className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {visibleKeywords.map((keyword) => (
              <li
                key={keyword}
                className="rounded-full bg-accent px-2.5 py-0.5 text-2xs font-medium tracking-wide text-accent-container-foreground"
              >
                {keyword}
              </li>
            ))}
            {remainingKeywords > 0 && (
              <li className="text-2xs font-medium tracking-wide text-muted-foreground">
                +{remainingKeywords} more
              </li>
            )}
          </ul>
        )}
        <Typography variant="caption-sm" as="span" className="ml-auto text-muted-foreground">
          {dataset.distribution_count.toLocaleString()} files
          <span aria-hidden className="px-1.5">·</span>v{dataset.version}
        </Typography>
      </footer>
    </Card>
  );
}

export { SearchResultCard };
